import {IMAGE_MIME_TYPE} from '@mantine/dropzone';
import type {
  PersonnelRequest,
  PersonnelResponse} from 'src/api/ProactApiClient';
import {
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import type {IFormField, ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

const JobCodeField: IFormFieldV2 = {
  name: 'jobCode',
  label: 'Job Code',
  type: 'text',
  validation: {
    maxLength: 100,
  },
};

const JobTitleField: IFormFieldV2 = {
  name: 'jobTitle',
  label: 'Job Title',
  type: 'text',
};

const SupervisorField: IFormFieldV2 = {
  name: 'supervisorId',
  label: 'Supervisor',
  type: 'person',
};

const CONTRACTOR_SUPERVISOR_DEFAULT_DESC =
  'Managed by Vendor. The supervisor is automatically set based on the vendor manager.';

const CONTRACTOR_SUPERVISOR_VENDOR_CHANGED_DESC =
  'The supervisor will be updated to the new vendor\'s manager when saved.';

const ContractorSupervisorField = (vendorChanged = false): IFormFieldV2 => ({
  name: 'supervisorId',
  label: 'Supervisor',
  type: 'person',
  disabled: true,
  description: vendorChanged
    ? CONTRACTOR_SUPERVISOR_VENDOR_CHANGED_DESC
    : CONTRACTOR_SUPERVISOR_DEFAULT_DESC,
});

const DepartmentField = (departments?: ISelectOption[]): IFormFieldV2 => ({
  name: 'departmentId',
  label: 'Department',
  type: 'autocomplete',
  options: departments || [],
});

export const PRIMARY_PROJECT_EMPTY_DESC =
  'This person needs a project assignment before a primary project can be set.';

const PrimaryProjectField = (projects: ISelectOption[]): IFormFieldV2 => ({
  name: 'primaryProjectId',
  label: 'Primary Project',
  type: 'autocomplete',
  options: projects,
  disabled: projects.length === 0,
  description: projects.length === 0 ? PRIMARY_PROJECT_EMPTY_DESC : undefined,
});

const VendorField = (
  vendors?: ISelectOption[],
  disabled?: boolean
): IFormFieldV2 => ({
  name: 'vendorId',
  label: 'Vendor',
  type: 'autocomplete',
  options: vendors || [],
  visibility: {
    isDefaultHidden: disabled || false,
    rules: [],
  },
});

const EmailField: IFormFieldV2 = {
  name: 'email',
  label: 'Email',
  type: 'text',
  required: true,
  textboxOptions: {
    validationType: 'email',
  },
};

const EmployeeIdField: IFormFieldV2 = {
  name: 'employeeId',
  label: 'Employee ID',
  type: 'text',
  required: true,
};

const NameField: IFormFieldV2 = {
  name: 'name',
  label: 'Name',
  type: 'text',
  required: true,
  textboxOptions: {
    validationType: 'name',
  },
};

const FirstNameField: IFormFieldV2 = {
  name: 'firstName',
  label: 'First Name',
  type: 'text',
  required: true,
  textboxOptions: {
    validationType: 'name',
  },
};

const LastNameField: IFormFieldV2 = {
  name: 'lastName',
  label: 'Last Name',
  type: 'text',
  required: true,
  textboxOptions: {
    validationType: 'name',
  },
};

const RoleField: IFormFieldV2 = {
  name: 'roleId',
  label: 'Role',
  type: 'select',
  required: true,
  options: [
    {id: 2, label: 'Admin'},
    {id: 3, label: 'User'},
    {id: 4, label: 'Observer'},
    {id: 5, label: 'Supervisor'},
    {id: 6, label: 'Safety Representative'},
    {id: 7, label: 'Notifications Only'},
  ],
};

const PhoneNumberField: IFormFieldV2 = {
  name: 'phoneNumber',
  label: 'Phone Number',
  type: 'phone',
};

const HiredAtField: IFormFieldV2 = {
  name: 'hiredAt',
  label: 'Hired At',
  type: 'date',
};

const ServiceDateAtField: IFormFieldV2 = {
  name: 'serviceDateAt',
  label: 'Service Date At',
  type: 'date',
};

const TerminatedAtField: IFormFieldV2 = {
  name: 'terminationAt',
  label: 'Termination Date',
  type: 'date',
};

const ReHiredAtField: IFormFieldV2 = {
  name: 'rehiredAt',
  label: 'Rehired At',
  type: 'date',
};

const BadgeExpirationAtField: IFormFieldV2 = {
  name: 'badgeExpirationAt',
  label: 'Badge Expiration Date',
  type: 'date',
  description:
    'When this date passes, the contractor will be automatically deactivated by the system. Leave empty to exempt from badge expiration.',
};

const IsSuspendedField: IFormFieldV2 = {
  name: 'isSuspended',
  label: 'Suspended',
  type: 'switch',
  switchOptions: {
    positiveText: 'Yes',
    negativeText: 'No',
  },
};

const IsLeaveOfAbsenceField: IFormFieldV2 = {
  name: 'isLeaveOfAbsence',
  label: 'Leave of Absence',
  type: 'switch',
  switchOptions: {
    positiveText: 'Yes',
    negativeText: 'No',
  },
};

const IsEligibleField: IFormFieldV2 = {
  name: 'isEligible',
  label: 'Eligibility',
  type: 'switch',
  switchOptions: {
    positiveText: 'Yes',
    negativeText: 'No',
  },
};

const IsBannedOnPropertyField: IFormFieldV2 = {
  name: 'isBannedOnProperty',
  label: 'Banned on Property',
  type: 'switch',
  switchOptions: {
    positiveText: 'Yes',
    negativeText: 'No',
  },
};

const IsMilitaryVeteranField: IFormFieldV2 = {
  name: 'isMilitaryVeteran',
  label: 'Military Veteran',
  type: 'switch',
  switchOptions: {
    positiveText: 'Yes',
    negativeText: 'No',
  },
};

const MilitaryServiceBranchField: IFormFieldV2 = {
  name: 'militaryServiceBranch',
  label: 'Military Service Branch',
  type: 'text',
};

export const UserAvatarField: IFormFieldV2 = {
  name: 'avatarUrl',
  label: 'Picture',
  type: 'file',
  description:
    'File type must be .jpg or .png. File size must be less than 1MB. Image should be a 1:1 aspect ratio (square).',
  fileUploadOptions: {
    allowMultiple: false,
    allowedFileTypes: IMAGE_MIME_TYPE,
    maxFileSize: 10240000,
    isHideUploadSuccessEnabled: true,
    renderUploadsAsImage: true,
  },
};

export const UserAvatarFieldLegacy: IFormField = {
  name: 'avatarUrl',
  label: 'Picture',
  htmlName: 'avatarUrl',
  ariaLabel: 'avatarUrl',
  type: 'imageUpload',
  formFieldTypeGroup: {
    imageUpload: true,
  },
  options: {
    allowMultiple: false,
    allowedFileTypes: IMAGE_MIME_TYPE,
    isHideUploadSuccessEnabled: true,
    maxFileSize: 10240000,
  },
  fullWidth: true,
  required: false,
  active: true,

  metadata: {
    helpText:
      'File type must be .jpg or .png. File size must be less than 1MB. Image should be a 1:1 aspect ratio (square).',
  },
};

export interface FormMetaSections {
  account: IFormFieldV2[];
  employment: IFormFieldV2[];
}

export const FormMetaBySections = (
  userType: UserTypeEnum,
  departments?: ISelectOption[],
  vendors?: ISelectOption[],
  vendorChanged = false,
  primaryProjects?: ISelectOption[]
): FormMetaSections => {
  // Undefined hides the field (create mode); empty array shows it disabled.
  const primaryProjectFields = primaryProjects ? [PrimaryProjectField(primaryProjects)] : [];

  if (userType === UserTypeEnum.Employee) {
    return {
      account: [
        NameField,
        FirstNameField,
        LastNameField,
        EmailField,
        PhoneNumberField,
        UserAvatarField,
      ],
      employment: [
        RoleField,
        EmployeeIdField,
        JobCodeField,
        JobTitleField,
        SupervisorField,
        DepartmentField(departments),
        ...primaryProjectFields,
        IsSuspendedField,
        IsLeaveOfAbsenceField,
        IsEligibleField,
        IsBannedOnPropertyField,
        IsMilitaryVeteranField,
        MilitaryServiceBranchField,
        HiredAtField,
        ServiceDateAtField,
        TerminatedAtField,
        ReHiredAtField,
      ],
    };
  }

  if (userType === UserTypeEnum.Contractor) {
    return {
      account: [
        NameField,
        FirstNameField,
        LastNameField,
        EmailField,
        PhoneNumberField,
        UserAvatarField,
      ],
      employment: [
        VendorField(vendors, false),
        ContractorSupervisorField(vendorChanged),
        DepartmentField(departments),
        ...primaryProjectFields,
        IsSuspendedField,
        IsLeaveOfAbsenceField,
        IsEligibleField,
        IsBannedOnPropertyField,
        HiredAtField,
        ServiceDateAtField,
        TerminatedAtField,
        ReHiredAtField,
        BadgeExpirationAtField,
      ],
    };
  }

  return {account: [], employment: []};
};

export const FormMeta = (
  userType: UserTypeEnum,
  departments?: ISelectOption[],
  vendors?: ISelectOption[],
  vendorChanged = false,
  primaryProjects?: ISelectOption[]
): IFormFieldV2[] => {
  const {account, employment} = FormMetaBySections(
    userType,
    departments,
    vendors,
    vendorChanged,
    primaryProjects
  );
  return [...account, ...employment];
};

export const IsPersonnelFlagged = (personnel: {
  isLeaveOfAbsence?: boolean;
  isEligible?: boolean;
  isSuspended?: boolean;
  isBannedOnProperty?: boolean;
}): boolean =>
  Boolean(
    personnel.isLeaveOfAbsence ||
      personnel.isEligible === false ||
      personnel.isSuspended ||
      personnel.isBannedOnProperty
  );

export const PersonnelPillConfig = (
  userType?: UserTypeEnum,
  isLeaveOfAbsence?: boolean,
  isEligible?: boolean,
  isSuspended?: boolean,
  badgeExpirationAt?: Date,
  primaryInstanceName?: string,
  isBannedOnProperty?: boolean
): PillProps[] => {
  const pills: PillProps[] = [];

  switch (userType) {
    case UserTypeEnum.Employee:
      pills.push({text: 'Employee', color: 'blue'});
      break;
    case UserTypeEnum.Contractor:
      pills.push({text: 'Contractor', color: 'yellow'});
      break;
    default:
      break;
  }

  if (isBannedOnProperty || isLeaveOfAbsence || !isEligible || isSuspended) {
    const text: string[] = [];
    if (isBannedOnProperty === true) {
      text.push('Banned');
    }

    if (isLeaveOfAbsence === true) {
      text.push('LOA');
    }

    if (isEligible === false) {
      text.push('Ineligible');
    }

    if (isSuspended === true) {
      text.push('Suspended');
    }
    const display = text.join(' | ');
    pills.push({text: display, color: 'red'});
  }

  if (badgeExpirationAt) {
    const d = new Date(badgeExpirationAt);
    const now = new Date();
    const isFuture = d > now;
    if (isFuture) {
      pills.push({
        text: `Badge Expires: ${d.toString().toShortDate()}`,
        color: 'blue',
      });
    } else {
      pills.push({text: 'Badge Expired', color: 'red'});
    }
  }

  if (primaryInstanceName) {
    pills.push({text: `Primary: ${primaryInstanceName}`, color: 'gray'});
  }

  return pills;
};

export const CreateDefaultPersonnel = (): PersonnelResponse => {
  return {
    active: true,
    isLeaveOfAbsence: false,
    isEligible: true,
    isSuspended: false,
    isBannedOnProperty: false,
    isMilitaryVeteran: false,
    existingAccount: false,
  } as unknown as PersonnelResponse;
};
export const MapPersonnelResponseToPersonnelRequest = (
  data: PersonnelResponse
): PersonnelRequest => {
  return {
    id: data.id,
    roleId: data.roleId,
    email: data.email,
    name: data.name,
    firstName: data.firstName ?? '',
    lastName: data.lastName ?? '',
    avatarUrl: data.avatarUrl ?? '',
    jobCode: data.jobCode ?? '',
    jobTitle: data.jobTitle ?? '',
    userType: data.userType,
    supervisorId: data.supervisorId,
    departmentId: data.departmentId,
    primaryProjectId: data.primaryProjectId,
    vendorId: data.vendorId,
    employeeId: data.employeeId ?? '',
    hiredAt: data.hiredAt,
    serviceDateAt: data.serviceDateAt,
    terminationAt: data.terminationAt,
    rehiredAt: data.rehiredAt,
    phoneNumber: data.phoneNumber,
    isLeaveOfAbsence: data.isLeaveOfAbsence,
    isEligible: data.isEligible,
    isSuspended: data.isSuspended,
    isBannedOnProperty: data.isBannedOnProperty,
    isMilitaryVeteran: data.isMilitaryVeteran,
    militaryServiceBranch: data.militaryServiceBranch,
  };
};

export const PersonnelDocumentTitle = 'Personnel';
